import React, { useState, useEffect } from 'react';
import ApiService from '../../../services/api';
import { getToken } from '../../../utils/auth';
import { useNotification } from '../../../context/NotificationContext';

interface Tag {
  id: number;
  name: string;
  value: any;
  createdAt: string;
  updatedAt: string | null;
  definitionId?: number;
  definition?: {
    id: number;
    name: string;
  };
}

interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const TagList = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState('');
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [newTag, setNewTag] = useState({ name: '', value: '' });
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editFormData, setEditFormData] = useState({ name: '', value: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [pagination, setPagination] = useState<PaginationMeta>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const { showNotification } = useNotification();

  // Debounce the name filter to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [nameFilter]);

  // Fetch tags when page or filter changes
  useEffect(() => {
    fetchTags(pagination.currentPage, debouncedNameFilter);
  }, [pagination.currentPage, debouncedNameFilter]);

  const fetchTags = async (page: number = 1, name: string = '') => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        setLoading(false);
        return;
      }

      const api = new ApiService(token);
      const response = await api.getTags(page, pagination.itemsPerPage, name);
      console.log('Tags API response:', response);
      
      if (response) {
        // Handle response with pagination metadata
        if (response && typeof response === 'object' && 'items' in response && Array.isArray(response.items)) {
          setTags(response.items);
          if ('meta' in response && response.meta) {
            const meta = response.meta as {
              currentPage?: number;
              totalPages?: number;
              totalItems?: number;
              itemsPerPage?: number;
            };
            setPagination({
              currentPage: meta.currentPage || page,
              totalPages: meta.totalPages || 1,
              totalItems: meta.totalItems || response.items.length,
              itemsPerPage: meta.itemsPerPage || pagination.itemsPerPage
            });
          }
        } 
        // Handle response with just an array
        else if (Array.isArray(response)) {
          setTags(response);
        } 
        else {
          console.warn('Unexpected API response format:', response);
          setTags([]);
        }
      } else {
        console.warn('No data in API response:', response);
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      showNotification('Failed to fetch tags. Please try again.', 'error');
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination({
        ...pagination,
        currentPage: newPage
      });
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameFilter(e.target.value);
    // Reset to first page when filter changes
    setPagination({
      ...pagination,
      currentPage: 1
    });
  };

  const handleClearFilter = () => {
    setNameFilter('');
    // Reset to first page when filter is cleared
    setPagination({
      ...pagination,
      currentPage: 1
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      await api.createTag({
        name: newTag.name,
        value: newTag.value ? JSON.parse(newTag.value) : null,
      });

      showNotification('Tag created successfully!', 'success');
      setNewTag({ name: '', value: '' });
      fetchTags(1, debouncedNameFilter); // Refresh and go back to first page
    } catch (error) {
      console.error('Error creating tag:', error);
      showNotification('Failed to create tag. Please try again.', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      await api.deleteTag(id);

      showNotification('Tag deleted successfully!', 'success');
      
      // If we're on a page with only one item and it's not the first page,
      // go to the previous page after deletion
      if (tags.length === 1 && pagination.currentPage > 1) {
        setPagination({
          ...pagination,
          currentPage: pagination.currentPage - 1
        });
      } else {
        fetchTags(pagination.currentPage, debouncedNameFilter);
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      showNotification('Failed to delete tag. Please try again.', 'error');
    }
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setEditFormData({
      name: tag.name,
      value: tag.value ? JSON.stringify(tag.value) : ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTag) return;

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      await api.updateTag(editingTag.id, {
        name: editFormData.name,
        value: editFormData.value ? JSON.parse(editFormData.value) : null,
      });

      showNotification('Tag updated successfully!', 'success');
      setShowEditModal(false);
      fetchTags(pagination.currentPage, debouncedNameFilter);
    } catch (error) {
      console.error('Error updating tag:', error);
      showNotification('Failed to update tag. Please try again.', 'error');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingTag(null);
  };

  // Pagination UI component
  const Pagination = () => {
    if (pagination.totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center mt-4 space-x-2">
        <button
          onClick={() => handlePageChange(1)}
          disabled={pagination.currentPage === 1}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === 1
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          First
        </button>
        <button
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 1}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === 1
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Previous
        </button>
        <div className="px-3 py-1 bg-blue-600 text-white rounded">
          {pagination.currentPage} of {pagination.totalPages}
        </div>
        <button
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage === pagination.totalPages}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === pagination.totalPages
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Next
        </button>
        <button
          onClick={() => handlePageChange(pagination.totalPages)}
          disabled={pagination.currentPage === pagination.totalPages}
          className={`px-3 py-1 rounded ${
            pagination.currentPage === pagination.totalPages
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Last
        </button>
      </div>
    );
  };

  // Edit Modal Component
  const EditModal = () => {
    if (!showEditModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold mb-4">Edit Tag</h3>
          <form onSubmit={handleEditSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag Name
              </label>
              <input
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter tag name"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag Value (Optional)
              </label>
              <input
                type="text"
                value={editFormData.value}
                onChange={(e) => setEditFormData({ ...editFormData, value: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter tag value"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeEditModal}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Update Tag
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Manage Tags</h2>

      {/* Filter section */}
      <div className="mb-4 bg-white p-4 rounded shadow">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex-grow">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={nameFilter}
                onChange={handleFilterChange}
                className="w-full p-2 pr-10 border border-gray-300 rounded"
                placeholder="Search tags..."
              />
              {nameFilter && (
                <button
                  onClick={handleClearFilter}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="md:self-end">
            {debouncedNameFilter && (
              <div className="text-sm text-gray-500 mt-1 md:mt-0">
                Filtering by: <span className="font-medium">{debouncedNameFilter}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 bg-white p-4 rounded shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag Name
            </label>
            <input
              type="text"
              value={newTag.name}
              onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter tag name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag Value (Optional)
            </label>
            <input
              type="text"
              value={newTag.value}
              onChange={(e) => setNewTag({ ...newTag, value: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter tag value"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
            >
              Create Tag
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="text-center py-4">Loading tags...</div>
      ) : tags && tags.length > 0 ? (
        <>
          <div className="bg-white rounded shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Definition
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tags.map((tag) => (
                  <tr key={tag.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{tag.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tag.value ? JSON.stringify(tag.value) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tag.definition ? tag.definition.name : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tag.createdAt ? new Date(tag.createdAt).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEdit(tag)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(tag.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination />
          <div className="mt-2 text-sm text-gray-500 text-center">
            Showing {tags.length} of {pagination.totalItems} tags
            {debouncedNameFilter && <span> matching "{debouncedNameFilter}"</span>}
          </div>
        </>
      ) : (
        <div className="text-center py-4 bg-white rounded shadow">
          {debouncedNameFilter 
            ? `No tags found matching "${debouncedNameFilter}". Try a different filter or create a new tag.`
            : 'No tags found. Create one above.'}
        </div>
      )}
      
      {/* Edit Modal */}
      <EditModal />
    </div>
  );
};

export default TagList; 